import React, { ReactElement, useEffect, useMemo, useState, useRef } from "react";
import { useQuery } from "react-query";
import { ListView, ListViewItemProps } from "@progress/kendo-react-listview";
import { useLocalization } from "@progress/kendo-react-intl";
import { enMessages } from "../../../../messages/en-US";
import LoadingSpinner from "../../../Shared/Components/LoadingSpinner";
import ErrorCard from "../../../Shared/Components/Cards/ErrorCard";
import templatesApiService from "../../Utilities/TemplatesApiService";

/** Normalizes bulk-update API response to the shape expected by the UI. */
function normalizeBulkUpdateResult(apiResult: any): {
  totalAssetCount: number;
  updatedAssetCount: number;
  results: Array<{ name: string; updated: boolean }>;
} {
  const results = Array.isArray(apiResult?.results)
    ? apiResult.results
    : Array.isArray(apiResult?.items)
      ? apiResult.items
      : [];
  const total = apiResult?.totalAssetCount ?? apiResult?.total_count ?? results.length;
  const updated = apiResult?.updatedAssetCount ?? apiResult?.updated_count ?? results.filter((r: any) => r?.updated ?? r?.success).length;
  const normalized = results.map((r: any) => ({
    name: r?.name ?? r?.assetName ?? r?.id ?? "—",
    updated: Boolean(r?.updated ?? r?.success),
  }));
  return { totalAssetCount: total, updatedAssetCount: updated, results: normalized };
}

/**
 * Update Assets step - saves template first (if saveTemplateFirst provided), then fetches bulk-update
 * via useQuery. Shows LoadingSpinner during both calls, then shows quantity summary (X/Y updated)
 * and list of assets that failed.
 */
interface AssetTemplateUpdateStepComponentProps {
  templateId: string;
  /** Called before bulk update to save template with current form data. Runs on mount, shows loading. */
  saveTemplateFirst?: () => Promise<void>;
  /** Called when bulk update completes (for parent footer) */
  onBulkUpdateComplete: (result: {
    totalAssetCount: number;
    updatedAssetCount: number;
    results: Array<{ name: string; updated: boolean }>;
  } | null) => void;
  onBulkUpdateStart?: () => void;
}

const FailedAssetItemRender = (props: ListViewItemProps) => {
  const item = props.dataItem as { name: string };
  return (
    <div className="k-listview-item flex items-center p-3 border-b border-gray-200 bg-red-50">
      <span className="font-medium text-red-800 p-2">{item.name}</span>
    </div>
  );
};

export default function AssetTemplateUpdateStepComponent(
  props: AssetTemplateUpdateStepComponentProps
): ReactElement {
  const { templateId, saveTemplateFirst, onBulkUpdateComplete, onBulkUpdateStart } = props;
  const localizationService = useLocalization();
  const [templateSaved, setTemplateSaved] = useState(!saveTemplateFirst);
  const [saveTemplateError, setSaveTemplateError] = useState<Error | null>(null);

  const hasRunSaveRef = useRef(false);
  useEffect(() => {
    if (!saveTemplateFirst || hasRunSaveRef.current) return;
    hasRunSaveRef.current = true;
    onBulkUpdateStart?.();
    saveTemplateFirst()
      .then(() => setTemplateSaved(true))
      .catch((err) => {
        setSaveTemplateError(err);
        onBulkUpdateComplete(null);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, [saveTemplateFirst]);

  const bulkUpdateQuery = useQuery(
    ["bulkUpdateAssets", templateId],
    () => templatesApiService.bulkUpdateAssets({ templateId }),
    { enabled: !!templateId && templateSaved }
  );

  const bulkUpdateResult = useMemo(
    () => (bulkUpdateQuery.data ? normalizeBulkUpdateResult(bulkUpdateQuery.data) : null),
    [bulkUpdateQuery.data]
  );

  useEffect(() => {
    if (bulkUpdateQuery.isLoading) {
      onBulkUpdateStart?.();
    } else if (bulkUpdateResult) {
      onBulkUpdateComplete(bulkUpdateResult);
    } else if (bulkUpdateQuery.isError) {
      onBulkUpdateComplete(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- sync to parent on query state change only
  }, [bulkUpdateQuery.isLoading, bulkUpdateResult, bulkUpdateQuery.isError]);

  const isSavingTemplate = saveTemplateFirst && !templateSaved && !saveTemplateError;
  const isLoading = isSavingTemplate || bulkUpdateQuery.isLoading;

  if (saveTemplateError) {
    return <ErrorCard />;
  }

  if (isLoading) {
    return (
      <div className="w-full pt-4 flex flex-col items-center justify-center min-h-[200px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (bulkUpdateQuery.isError) {
    return <ErrorCard />;
  }

  if (bulkUpdateResult) {
    const { totalAssetCount, updatedAssetCount, results } = bulkUpdateResult;
    const failedAssets = results.filter((r) => !r.updated).map((r) => ({ name: r.name }));
    const allSucceeded = totalAssetCount > 0 && updatedAssetCount === totalAssetCount;
    const noAssetsToUpdate = totalAssetCount === 0;
    const summaryText = localizationService
      .toLanguageString("custom.assetsBulkUpdateSummary", "{0}/{1} assetsupdated")
      .replace("{0}", String(updatedAssetCount))
      .replace("{1}", String(totalAssetCount));
    const successText = localizationService.toLanguageString(
      "custom.assetsAllUpdatedSuccess",
      enMessages.custom.assetsAllUpdatedSuccess
    );
    const allUpToDateText = localizationService.toLanguageString(
      "custom.assetsAllUpToDate",
      enMessages.custom.assetsAllUpToDate
    );

    return (
      <div className="w-full pt-4">
        <h3 className="text-lg font-semibold mb-4">
          {localizationService.toLanguageString("custom.updateSummary", enMessages.custom.updateSummary)}
        </h3>
        {noAssetsToUpdate ? (
          <div className="flex items-center gap-3 p-4">
            <svg
              className="w-8 h-8 flex-shrink-0 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-lg font-medium">{allUpToDateText}</p>
          </div>
        ) : allSucceeded ? (
          <div className="flex items-center gap-3 p-4">
            <svg
              className="w-8 h-8 flex-shrink-0 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-lg font-medium">{successText}</p>
          </div>
        ) : (
          <p className="text-lg font-medium text-gray-700 mb-4">{summaryText}</p>
        )}
        {failedAssets.length > 0 && (
          <>
            <p className="text-gray-600 mb-2">
              {localizationService.toLanguageString(
                "custom.assetsUpdateFailedList",
                "Not updated:"
              )}
            </p>
            <ListView
              data={failedAssets}
              item={FailedAssetItemRender}
              style={{ width: "100%" }}
              className="border border-red-200 rounded-lg overflow-hidden"
            />
          </>
        )}
      </div>
    );
  }

  return (
    <div className="w-full pt-4">
      <h3 className="text-lg font-semibold mb-4">
        {localizationService.toLanguageString("custom.updateSummary", enMessages.custom.updateSummary)}
      </h3>
      <p className="text-gray-600">
        Click Save to update all assets using this template.
      </p>
    </div>
  );
}
